import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { countries, usStates } from '../mock/mockData';
import '../styles/BookMeeting.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookMeeting = () => {
  const [step, setStep] = useState(1);
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [hasHighIntent, setHasHighIntent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCountrySelect = (selectedCountry) => {
    setCountry(selectedCountry);
    
    // If EMEA or APAC, route directly
    if (selectedCountry === 'EMEA (Europe, Middle East, and Africa)' || 
        selectedCountry === 'APAC (Asia-Pacific)') {
      handleSubmit(selectedCountry, null, null);
    } else {
      // Go to state selection for US
      setStep(2);
    }
  };

  const handleStateSelect = (selectedState) => {
    setState(selectedState);
    setStep(3);
  };

  const handleIntentSelect = (intent) => {
    setHasHighIntent(intent);
    handleSubmit(country, state, intent);
  };

  const handleSubmit = async (selectedCountry, selectedState, intent) => {
    setIsSubmitting(true);
    
    try {
      // Call backend API to save meeting request and get booking URL
      const response = await axios.post(`${API}/meeting-request`, {
        country: selectedCountry,
        state: selectedState,
        has_high_intent: intent
      });
      
      // Redirect to the booking URL returned by backend
      window.location.href = response.data.booking_url;
    } catch (error) {
      console.error('Error submitting meeting request:', error);
      alert('An error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(2);
      setHasHighIntent(null);
    } else if (step === 2) {
      setStep(1);
      setState('');
      setCountry('');
    }
  };

  return (
    <div className="book-meeting-container">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>PourMyBev - Book a meeting</title>
        <meta name="title" content="PourMyBev - Book a meeting" />
        <meta name="description" content="Talk to a self-pour expert to Reduce overhead by 75%, fast-track ROI within a year, and boost sales volume by 50%" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://app.pourmybeer.com/book" />
        <meta property="og:title" content="PourMyBev - Book a meeting" />
        <meta property="og:description" content="Talk to a self-pour expert to Reduce overhead by 75%, fast-track ROI within a year, and boost sales volume by 50%" />
        <meta property="og:image" content="https://app.pourmybeer.com/favicon.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://app.pourmybeer.com/book" />
        <meta property="twitter:title" content="PourMyBev - Book a meeting" />
        <meta property="twitter:description" content="Talk to a self-pour expert to Reduce overhead by 75%, fast-track ROI within a year, and boost sales volume by 50%" />
        <meta property="twitter:image" content="https://app.pourmybeer.com/favicon.png" />
      </Helmet>
      <div className="book-meeting-content">
        {/* Header */}
        <div className="header-section">
          <img 
            src="https://brevityassets.brevity.workers.dev/colonial-aquamarine-goldfish/dual-vertical-w-seperator-bl-1-3UCEnUYCTA7g.svg" 
            alt="Pour My Beer Logo" 
            className="logo"
          />
          
          <a href="https://pourmybeer.com" className="back-link">
            <ArrowLeft size={16} />
            Back main site
          </a>
        </div>

        {/* Main Content */}
        <div className="main-content">
          <h1 className="title">Book a meeting</h1>
          <p className="subtitle">
            Meet with a Self-Pour Expert
            <br />
            and Discover a More Profitable Way to Pour.
          </p>

          {/* Step 1: Country Selection */}
          {step === 1 && (
            <div className="question-card animate-fade-in">
              <h2 className="question-title">
                What country/region will your self-pour system be located?
              </h2>
              <Select onValueChange={handleCountrySelect} value={country}>
                <SelectTrigger className="select-trigger">
                  <SelectValue placeholder="Select a country or region..." />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Step 2: State Selection (US only) */}
          {step === 2 && (
            <div className="question-card animate-fade-in">
              <button onClick={handleBack} className="back-button">
                <ArrowLeft size={20} />
                Back
              </button>
              <h2 className="question-title">
                What state will your self-pour system be located?
              </h2>
              <Select onValueChange={handleStateSelect} value={state}>
                <SelectTrigger className="select-trigger">
                  <SelectValue placeholder="Select a state..." />
                </SelectTrigger>
                <SelectContent className="state-select-content">
                  {usStates.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Step 3: Intent Selection (US only) */}
          {step === 3 && (
            <div className="question-card animate-fade-in">
              <button onClick={handleBack} className="back-button">
                <ArrowLeft size={20} />
                Back
              </button>
              <h2 className="question-title">
                Do you intend to implement a self-pour system in the next 12 months?
              </h2>
              <div className="intent-buttons">
                <Button
                  onClick={() => handleIntentSelect(true)}
                  className="intent-button yes-button"
                  disabled={isSubmitting}
                >
                  YES
                </Button>
                <Button
                  onClick={() => handleIntentSelect(false)}
                  className="intent-button no-button"
                  disabled={isSubmitting}
                >
                  NO
                </Button>
              </div>
            </div>
          )}

          {/* Bottom CTA */}
          {step === 1 && (
            <div className="bottom-cta animate-fade-in">
              <p className="cta-text">Not ready for a meeting?</p>
              <a 
                href="https://pourmybeer.com/request-a-quote/" 
                className="cta-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Request a Quote →
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookMeeting;
